import Vue from 'vue'
import Vuex from 'vuex'
import salesModule from "@/store/modules/Sale/sales-module";
import emailModule from "@/store/modules/Sale/email-module"
import ticketModule from './modules/ticket-module'
import stockModule from "@/store/modules/Stock/stockmodule";
import stockCategories from "@/store/modules/Stock/stockcategory-module";
import floatModule from "@/store/modules/Sale/float-module";
import tableModule from "@/store/modules/Sale/table-module";
import cookingInstruction from "@/store/modules/Sale/cookingInstruction";
import stockTake from '@/store/modules/Stock/stock-take-module';
import paymentMethods from '@/store/modules/Sale/payment-methods-module.js';
import accounts from "@/store/modules/Accounts/accounts";
import kazangModule from "@/store/modules/Sale/kazang_module";
import supplierModule from "@/store/modules/supplier-module.js";
import stockReceivedModule from "@/store/modules/Stock/stock-received-module";
import stockTransferModule from "@/store/modules/Stock/stock-transfer-module";
import kazangPayment from "@/store/modules/Kazang/payment";
import kazangSignUp from "@/store/modules/Kazang/sign-up-module";
import customInstall from "@/store/modules/Sale/custom-install-module";
import billingModule from "@/store/modules/billing-module";
import backOfficeModule from "@/store/modules/backoffice-module";
import timeModule from "@/store/modules/time-module";
import user_security_module from "@/store/modules/user/user_security_module";
import vatCalculatorModule from "@/store/modules/vat_calculator";
import axios from "axios";
import { Storage } from "@/services/Storage"

import { responses } from "@/utils/responses";
import userModule from "@/store/modules/user_module";
import taxModule from "@/store/modules/tax-module";
import layByModule from "@/store/modules/Sale/lay-by-modules";
import loyaltyModule from "@/store/modules/Sale/loyalty-module";
import router from "@/router";
import queryStringify from "qs-stringify";
import Swal from "sweetalert2";
import ticket_notes_module from "@/store/modules/ticket_notes_module";
import theme from './modules/ui/theme-module';
import windowModule from './modules/ui/window-module';
import orders from './modules/orders-module';
import { hardwareBridge } from '@/services/hardwareBridge';
import syncService from '@/services/syncService';
import connectivityMonitor from '@/services/connectivityMonitor';
import settingsOutbox from '@/services/settingsOutbox';
import { resetCounter } from '@/services/invoiceNumberService';
import { Device } from '@/services/Device';

//vuex persist
import VuexPersistence from 'vuex-persist';
import { idbTransactionalStorage, restoreState, saveState } from '@/services/idbStorage';

Vue.use(Vuex)

/**
 * updates module — see docs/exec-plans/active/2026-06-23-app-update-flow.md
 * Holds the in-memory state for the in-app update flow. Mutations are
 * driven by the renderer-side updateService.init() which subscribes to
 * IPC events emitted by electron/autoUpdater.js.
 */
const updatesModule = {
    namespaced: true,
    state: () => ({
        available: false,
        currentVersion: '',
        latestVersion: '',
        isNewer: false,
        meta: null,            // full autosigner metadata (version/sha256/sizeBytes/downloadUrl/notes/releaseDate)
        downloading: false,
        progressPercent: 0,
        downloadedPath: '',
        downloadedShaOk: false,
        lastCheckedAt: null,
        error: null
    }),
    getters: {
        updatesAvailable: (state) => !!state.available,
        updatesCurrentVersion: (state) => state.currentVersion || '',
        updatesLatestVersion: (state) => state.latestVersion || '',
        updatesIsNewer: (state) => !!state.isNewer,
        updatesMeta: (state) => state.meta,
        updatesDownloading: (state) => !!state.downloading,
        updatesProgressPercent: (state) => Math.max(0, Math.min(100, Number(state.progressPercent) || 0)),
        updatesDownloadedPath: (state) => state.downloadedPath || '',
        updatesDownloadedShaOk: (state) => !!state.downloadedShaOk,
        updatesLastCheckedAt: (state) => state.lastCheckedAt,
        updatesError: (state) => state.error,
        updatesIsDownloaded: (state) => !!state.downloadedPath && !!state.downloadedShaOk
    },
    mutations: {
        setAvailable: (state, value) => { state.available = !!value; },
        setCurrentVersion: (state, value) => { state.currentVersion = value || ''; },
        setLatestVersion: (state, value) => { state.latestVersion = value || ''; },
        setIsNewer: (state, value) => { state.isNewer = !!value; },
        setMeta: (state, value) => { state.meta = value || null; },
        setDownloading: (state, value) => { state.downloading = !!value; },
        setProgressPercent: (state, value) => { state.progressPercent = Number(value) || 0; },
        setDownloadedPath: (state, value) => { state.downloadedPath = value || ''; },
        setDownloadedShaOk: (state, value) => { state.downloadedShaOk = !!value; },
        setLastCheckedAt: (state, value) => { state.lastCheckedAt = value || null; },
        setError: (state, value) => { state.error = value || null; },
        reset: (state) => {
            state.available = false;
            state.currentVersion = '';
            state.latestVersion = '';
            state.isNewer = false;
            state.meta = null;
            state.downloading = false;
            state.progressPercent = 0;
            state.downloadedPath = '';
            state.downloadedShaOk = false;
            state.lastCheckedAt = null;
            state.error = null;
        }
    },
    actions: {
        checkForUpdate: async ({ commit }) => {
            const updateService = await import(/* webpackChunkName: "update-service" */ '@/services/updateService');
            commit('setError', null);
            return updateService.checkForUpdate();
        },
        downloadUpdate: async ({ commit }) => {
            commit('setError', null);
            commit('setDownloading', true);
            commit('setProgressPercent', 0);
            const updateService = await import(/* webpackChunkName: "update-service" */ '@/services/updateService');
            const result = await updateService.downloadUpdate();
            // Progress is driven by IPC events; ensure the spinner stops
            // even if the renderer missed the closing event.
            if (!result || !result.ok) {
                commit('setDownloading', false);
                if (result && result.code) {
                    commit('setError', { code: result.code, message: result.message || '' });
                }
            }
            return result;
        },
        installUpdate: async () => {
            const updateService = await import(/* webpackChunkName: "update-service" */ '@/services/updateService');
            return updateService.installUpdate();
        }
    }
};

const vuexLocal = new VuexPersistence({
    storage: idbTransactionalStorage,
    asyncStorage: true,
    key: "vuex",
    restoreState,
    saveState,
    reducer: (state) => {
        const nextState = {
            ...state,
            stockModule: state.stockModule ? { ...state.stockModule } : state.stockModule
        };

        if (nextState.stockModule) {
            // Stock is persisted separately in IndexedDB via Storage.* calls.
            // Exclude large stock arrays/maps from VuexPersist to avoid heavy serialization.
            delete nextState.stockModule.stock;
            delete nextState.stockModule.stockProducts;
            delete nextState.stockModule.stockCache;
            delete nextState.stockModule.stockSetting;
            delete nextState.stockModule.stockSettingMap;
            delete nextState.stockModule.stockItem;
            // Ephemeral full-sync overlay must not survive restart stuck active.
            delete nextState.stockModule.stockSyncProgress;
        }

        if (nextState.salesModule) {
            // isSaleProcessing is a transient re-entry guard for processTransaction.
            // Never persist it: if the app dies mid-sale the snapshot would restore it
            // as true and permanently block all new sales ("Ignored duplicate trigger
            // while processing" - 2026-09-23 Mission Press incident).
            nextState.salesModule = { ...nextState.salesModule, isSaleProcessing: false };
        }

        return nextState;
    }
});

function cleanUpState(state) {
    return JSON.parse(JSON.stringify(state));
}

let CurrentTime = function () {
    let date = new Date();
    let d = date.toISOString().split('T')[0]
    let hour = date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
    let minute = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
    let t = hour + ':' + minute + 'hrs';
    return d + ` at ` + t;
}

const store = new Vuex.Store({
    state: {

        deviceRegistrationID: null,
        ResetCount: 0,
        registeredStores: [],
        defaultStore: null,
        mainCustomerID: null,
        customerStores: [],
        device_settings: null,
        systemSettings: {},
        lastUpdated: '',
        registrationDetails: null,
        zedMobileStatus: null,
        refundReasons: [
            {
                code: "01",
                description: "Missing Quantity"
            },
            {
                code: "02",
                description: "Missing Item"
            },
            {
                code: "03",
                description: "Damaged"
            },
            {
                code: "04",
                description: "Wasted"
            },
            {
                code: "05",
                description: "Raw Material Shortage"
            },
            {
                code: "06",
                description: "Refund"
            },
            {
                code: "07",
                description: "Wrong Customer TPIN"
            }
        ],
        loading: false,
        isHydrated: false,
        isUpdateAvailable: false,
        isAndroid: window.androidMode,
        tot: false,
        overrideDetails: ``,
        zedMobileCategories: [],
        isSmartInvoicing: false,
        showSupportNotice: true,
        shortcuts: [],
    },
    getters: {
        isHydrated: (state) => state.isHydrated,

        getResetCount: (state) => state.ResetCount,
        getDeviceRegistrationID: (state) => state.deviceRegistrationID,
        getSmartInvoicingStatus: (state) => state.isSmartInvoicing,
        /**
         * Returns true only when the currently selected store has onVat === true.
         * Used by printProvisionalReceipt to decide whether to print VAT values
         * or zero them on offline provisional receipts. Returns false for any
         * missing/undefined/non-boolean value so undefined stores default to
         * VAT-zeroed printing (per Felix's strict-mode decision).
         * @param {Object} state Vuex root state
         * @returns {boolean}
         */
        getOnVatStatus: (state) => state.defaultStore?.onVat === true,
        getRegisteredStores: (state) => state.registeredStores,
        getRefundReasons: (state) => state.refundReasons,
        getOverrideDetails: (state) => state.overrideDetails,
        getRegistrationDetails: (state) => state.registrationDetails,
        getLoadingState: (state) => state.loading,
        isTot: (state) => state.tot,
        getUpdateAvailability: (state) => state.isUpdateAvailable,
        getAndroid: (state) => state.isAndroid,
        getDefaultStore: (state) => state.defaultStore,
        getMainCustomerID: (state) => state.mainCustomerID,
        getCustomerStores: (state) => state.customerStores,
        getDeviceSettings: (state) => state.device_settings,
        getLasUpdated: (state) => state.lastUpdated,
        getSystemSettings: (state) => state.systemSettings,
        getZedMobileCategories: (state) => state.zedMobileCategories,
        getZedMobileStatus: (state) => !!state.zedMobileStatus,
        getZedMobileApiKey: (state) => state.zedMobileStatus,
        getShortcuts: (state) => state.shortcuts,
        currentDate: () => {
            let date = new Date();
            return date.toISOString().split('T')[0];
        },

        currentTime: () => {
            let date = new Date();
            let d = date.toISOString().split('T')[0]
            let hour = date.getHours() < 10 ? '0' + date.getHours() : date.getHours();
            let minute = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();
            let t = hour + ':' + minute;
            return d + `T` + t;
        },
    },
    mutations: {
        setResetCount: (state, count) => { state.ResetCount = count },

        setSmartInvoicingStatus: (state, payload) => state.isSmartInvoicing = payload,
        setDeviceRegistrationID: (state, payload) => state.deviceRegistrationID = payload,
        toggleNavigation: (state, width) => state.navigation = width,
        setLoadingState: (state, payload) => state.loading = payload,
        setTot: (state, payload) => state.tot = payload,
        setUpdateAvailability: (state, payload) => state.isUpdateAvailable = payload,
        setOverrideDetails: (state, payload) => state.overrideDetails = payload,
        setRegisteredStores: async (state, data) => {
            if (!data) data = await Storage.getAllStores();
            state.registeredStores = data
        },
        setLastUpdated: (state, payload) => state.lastUpdated = payload,
        setDeviceSettings: async (state, payload) => {
            if (!payload) {
                payload = await Storage.getDeviceSettings()
            }
            state.device_settings = payload
        },
        getRefundReasons: (state) => state.refundReasons,
        setSystemSettings: async (state, payload) => {
            if (!payload) payload = Storage.getSystemSettings();
            state.systemSettings = payload
        },

        setShortcuts: async (state, payload) => {
            state.shortcuts = payload
        },

        setRegistrationDetails: async (state, payload) => {
            if (!payload) payload = await Storage.getRegistrationDetails();
            state.registrationDetails = payload
        },
        setZedMobileStatus: async (state, payload) => {
            if (!payload) payload = Storage.getZedMobileStatus()
            state.zedMobileStatus = payload
        },
        setZedMobileCategories: async (state, payload) => state.zedMobileCategories = payload,

        setDefaultStore: (state, data) => {
            state.defaultStore = data;
        },
        setMainCustomerID: (state, data) => {
            state.mainCustomerID = data;
        },
        setCustomerStores: (state, data) => {
            state.customerStores = data;
        },
        // Store-only sign-out Vuex wipe. Keep franchise identity:
        // registeredStores, admin, mainCustomerID, customerStores.
        // Clear the selected store and store-session fields only.
        logOutofStore: (state) => {
            state.users = [];
            state.defaultStore = null;
            state.registrationDetails = null;
        },
        setShowSupportNotice(state, value) {
            state.showSupportNotice = value;
            localStorage.setItem('showSupportNotice', JSON.stringify(value));
        },
        setHydrated(state, value) {
            state.isHydrated = value;
        },
    },
    actions: {
        startSyncService({ commit, state }) {
            if (!state.isHydrated) {
                //    console.log('[Store] Waiting for hydration before starting sync service');
                return;
            }
            //  console.log('[Store] Starting background sync service');
            syncService.start();
        },
        /**
         * Load keyboard shortcuts from API
         * Fetches dynamic shortcuts from the backend and falls back to defaults on error
         */
        loadShortcuts: async ({ commit }) => {


            // Default shortcuts configuration
            const defaultShortcuts = {
                "Account": "F4",           // Process account sale when customer selected (POS screen)
                "Reprint": "PRINTSCREEN",  // Reprint invoice (viewInvoice page)
                "splitPay": "PAGE_UP",     // Open split payment screen (POS screen)
                "LoadTable": "TAB",        // Navigate to tables page (POS screen)
                "Cash": "F2",              // Process cash payment (POS screen)
                "PriceCheck": "SLASH",     // Open item search modal (POS screen)
                "LogOff": "END",           // Log out user (any screen)
                "PriceChange": "F9",       // Change price on selected item (POS screen)
                "CashBack": "",            // Reserved for future implementation
                "Void": "BACK_SPACE",      // Void last item (POS screen)
                "MoneyToSafe": "PAGE_DOWN",// Money to safe operation (tables screen)
                "ExitPos": "ESCAPE",       // Close application (any screen)
                "cgratePay": "SPACE",      // Process Kazang payment (POS screen)
                "OpenDrwer": "F2",         // Open cash drawer (any screen)
                "PaySupplier": "PAGE_UP",  // Pay supplier/payout (tables screen)
                "CashUp": "F12",           // Navigate to cashup screen (any screen)
                "Card": "F3",              // Process card payment (POS screen)
                "Payment": "SPACE",        // Process payment / PAY button (POS screen)
                "Refund": "END",           // Refund invoice (viewInvoice page)
                "SaveTable": "INSERT",     // Save table (POS screen)
                "VoidAll": "DELETE",       // Void all items (POS screen)
                "forexPay": "F8"
            };

            try {
                const storeDB = await Storage.getCurrentStoreDB();

                if (!storeDB) {
                    console.warn('[Shortcuts] No store database found, using defaults');
                    await commit('setShortcuts', defaultShortcuts);
                    return;
                }


                const response = await axios.get(`${backOfficeTomcatApi}keys?DB=${storeDB}`);

                if (response.data && response.data.length > 0) {
                    const apiShortcuts = response.data[0];
                    //     console.log('[Shortcuts] Successfully loaded from API:', apiShortcuts);
                    await commit('setShortcuts', apiShortcuts);
                } else {
                    console.warn('[Shortcuts] API returned empty data, using defaults');
                    await commit('setShortcuts', defaultShortcuts);
                }
            } catch (error) {
                console.error('[Shortcuts] Failed to load from API:', error.message);
                //console.log('[Shortcuts] Falling back to default shortcuts');
                await commit('setShortcuts', defaultShortcuts);
            }
        },

        initializeStore({ commit }) {
            const savedState = localStorage.getItem('showSupportNotice');
            if (savedState) {
                commit('setShowSupportNotice', JSON.parse(savedState));
            }
        },
        /**
         * Store-only sign-out. Clears store binding + store-session Vuex and
         * disk via Storage.logOutofStore; keeps franchise Welcome credentials
         * and store picker list (admin / registeredStores / mainCustomerID).
         */
        signOutOfStore: async (context) => {
            let user = await Storage.getCurrentUser();
            if (!!user && isValidString(user.Username)) {
                context.dispatch('recordLogout', user.Username);
            }
            // Unbind device StoreDB so getDefaultStore cannot rehydrate the
            // previous store from hardware after a store-only sign-out.
            if (!window.androidMode) {
                try {
                    if (window?.hardwareIPC || window?.electronBridge) {
                        await hardwareBridge.setStoreDB('none');
                    }
                } catch (e) {
                }
                try {
                    if (typeof hardwareApi !== 'undefined' && hardwareApi) {
                        await axios.get(hardwareApi + `utils/setDb/none`);
                    }
                } catch (e) {
                }
            }
            // Disk wipe first (default_store + stock + store keys). Does not
            // touch admin or all_stores.
            try {
                await Storage.logOutofStore();
            } catch (e) {
                console.warn('[signOutOfStore] Storage.logOutofStore failed:', e?.message || e);
            }
            // Vuex: clear store session only — keep registeredStores / admin /
            // mainCustomerID / customerStores for the store picker.
            await Promise.all([
                context.commit(`setCurrentUser`, {
                    Type: "",
                    Username: "",
                    id: "",
                    Surname: "",
                    Name: "",
                    Password: ""
                }),
                context.commit(`setDefaultStore`, null),
                context.commit(`setRegistrationDetails`, null),
                context.commit(`setDeviceSettings`, null),
                context.commit(`stockByPLU`, { items: [], persist: false }),
                context.commit(`setStockSetting`, []),
                context.commit(`setAllUsers`, []),
                context.commit(`setAllSecurities`, []),
                context.commit(`setDiscountSpecials`, []),
                context.commit(`setComboSpecials`, []),
                context.commit(`setMultiSpecials`, []),
                context.commit(`setBaseCurrency`, { Symbol: `K` }),
                context.commit('setCurrentSale', 0),
                context.commit('setCurrentLines', []),
                context.commit('setDebtors', []),
                context.dispatch('setCustomer', ''),
                context.commit(`logOutofStore`),
            ]);
        },

        /**
         * Leave the bound store (full store wipe) and open the store picker.
         * Keeps franchise Welcome credentials. Use for Switch Store / Select Store.
         */
        switchStore: async (context) => {
            await context.dispatch('signOutOfStore');
            return router.push({ name: 'login' });
        },

        /**
         * Full Sign Out: store wipe + clear franchise Welcome footprint
         * (admin credentials, store list, mainCustomerID). Lands on franchise
         * login step — not the store picker.
         */
        signOutOfFranchise: async (context) => {
            // End server device-login session so tryDeviceAutoLogin cannot
            // skip franchise Welcome after Sign Out (not used on Switch Store).
            try {
                const deviceId = await Device.getDeviceId();
                if (deviceId) {
                    try {
                        await axios.put(`${backOfficeTomcatApi}mobileUser/device-login/logout`, null, {
                            params: { deviceId }
                        });
                    } catch (err) {
                        // No active LOGGED_IN record is expected in some cases; ignore 404.
                        if (err?.response?.status !== 404) {
                            console.warn('[signOutOfFranchise] Device logout failed:', err?.message || err);
                        }
                    }
                }
            } catch (err) {
                console.warn('[signOutOfFranchise] Device id failed:', err?.message || err);
            }
            await context.dispatch('signOutOfStore');
            try {
                await Storage.setAdminUser({});
            } catch (e) {
                console.warn('[signOutOfFranchise] clear admin failed:', e?.message || e);
            }
            try {
                await Storage.setAllStores([]);
            } catch (e) {
                console.warn('[signOutOfFranchise] clear all_stores failed:', e?.message || e);
            }
            try {
                await context.commit('setAdmin', {});
            } catch (_) { /* ignore */ }
            try {
                await context.commit('setRegisteredStores', []);
            } catch (_) { /* ignore */ }
            try {
                context.commit('setMainCustomerID', null);
            } catch (_) { /* ignore */ }
            try {
                context.commit('setCustomerStores', []);
            } catch (_) { /* ignore */ }
            try {
                await context.commit('setMobileUser', null);
            } catch (_) { /* ignore */ }
            return router.push({ name: 'login' });
        },

        posLogout: async (context, payload) => {
            let loginedIn = context.getters.getCurrentUser
            let user = loginedIn.Username;
            if (user === 'Admin') {
                user = localStorage.getItem('supportAgent');
                localStorage.removeItem('supportAgent');
            }
            const auth = {
                Type: "",
                Username: "",
                id: "",
                Surname: "",
                Name: "",
                Password: ""
            }
            await context.commit(`setCurrentUser`, auth)
            await Storage.setCurrentUser(auth)
            await context.dispatch('recordLogout', user)
            return router.push({ name: 'poslogin' })
        },
        async migrateLocalStorageToIndexedDB(context) {
            try {
                const theme = localStorage.getItem('theme');
                // Step 1: Retrieve all keys from localStorage
                const keys = Object.keys(localStorage);

                // Step 2: Loop through each key and migrate to IndexedDB
                for (const key of keys) {
                    const value = localStorage.getItem(key);
                    if (value) {
                        try {
                            // Attempt to parse the value as JSON
                            const parsedValue = JSON.parse(value);
                            await set(key, parsedValue); // Store in IndexedDB
                        } catch (error) {
                            console.error(`Error parsing value for key "${key}":`, error);
                            // Store the raw value if parsing fails
                            await set(key, value);
                        }
                    }
                }

                // Step 3: Clear localStorage after migration
                localStorage.clear();
                if (theme === 'light' || theme === 'dark') {
                    localStorage.setItem('theme', theme);
                }

            } catch (error) {
                console.error('Error migrating data:', error);
                throw error; // Re-throw the error for handling in the component
            }
        },
        checkSmartInvoicing: async (context) => {
            let storeDB = await Storage.getCurrentStoreDB();
            if (!storeDB) return;
            let user = await Storage.getCurrentUser();

            try {
                let data = await axios.get(franchiseTomcatApi + `GRV/${storeDB}/checkSmartInvoicing`)
                if (data) {
                    context.commit(`setSmartInvoicingStatus`, data)
                    let isLive = context.getters.getSmartInvoicingLive;
                    if (!data && isLive) {
                        try {
                            await context.dispatch(`checkIfStoreDocsSubmitted`)
                            let hasDocs = context.getters.getDocSubmissionStatus;
                            if (!hasDocs && !!user) {
                                //     //     return Swal.fire({
                                //     //         title: 'Register for Smart Invoicing Now',
                                //     //         text: 'You are not registered for smart invoicing. Please register now.',
                                //     //         icon: 'warning',
                                //     //         showCancelButton: true,
                                //     //         showCloseButton : true,
                                //     //         allowOutsideClick : false,
                                //     //         confirmButtonText: 'Register',
                                //     //         cancelButtonText: 'Cancel'
                                //     //     }).then((result) => {
                                //     //         if (result.isConfirmed) {
                                //     //             return router.push({name: 'smart-invoicing-setup'});
                                //     //         }
                                //     //     });
                                //     // }
                                // })
                            }
                        }
                        catch (e) {
                            console.error("failed to check because usert is offline")
                        }
                    }
                }
            }
            catch (e) {
                console.error("failed to check because usert is offline")
            }
        },
        syncDataLogin: async (context,{  dispatch, commit, getters }) => {
            // If device is offline, skip network sync silently
            try {
                const online = connectivityMonitor.getStatus();
                if (!online) {
                    // console.log('[Sync] Skipping syncData: device is offline');
                    return 'offline-skip';
                }
            } catch (e) { }

            await Storage.getDefaultStore();
            await dispatch('getDeviceSettings')
            await dispatch("getCustomerDetails")
            let validate = await dispatch(`validatePCRegistration`);
            if (!validate.success) {
                await responses.deviceErr(validate.message, `Registration failed`)
                commit(`setDefaultStore`, null);
                return router.push({ name: `setup` })
            }

            let userCount = await context.getters.countNonLytSoftUsers;
            console.log("userCount",userCount)
            if (userCount === 0 || !userCount) {
                return router.push({ name: 'create-initial-user' })
            }
            return router.push({ name: 'poslogin' })

            return axios.all([
                dispatch("getExpiryDateByCustomerID"),
                dispatch("updateToLatestVersion"),
                dispatch('getAllStock'),
                dispatch('getAllUsers'),
                dispatch('getBaseCurrency'),
                dispatch('getAllCurrencies'),
                dispatch("getCategories"),
                dispatch("isKazangIntegrated"),
                dispatch("getAllPaymentMethods"),
                dispatch("getAllActiveSpecials"),
                dispatch("getAllTaxLabels"),
                dispatch("getAllStores"),
                dispatch("getAllFranchiseSettings"),
                dispatch("getAllSecurities"),
                dispatch("getSystemSettings"),
                dispatch("isLoyaltyActivated"),
                dispatch("isZedMobileActivated"),
            ]).then(() => {
                commit("setKazangSession");
                commit('setLastUpdated', CurrentTime())
            });

        },
        syncCookingInstructions: async () => {
            const isNativeMobile = (() => {
                try {
                    if (window?.androidMode) return true;
                    return !!(window?.Capacitor &&
                        typeof window.Capacitor.isNativePlatform === 'function' &&
                        window.Capacitor.isNativePlatform());
                } catch (_) {
                    return !!window?.androidMode;
                }
            })();
            if (isNativeMobile) {
                return null;
            }
            let store = await Storage.getDefaultStore();
            if (!store) return null;
            return axios.get(`${serverApi}cookinginstruction/${store.StoreDB}/all`)
                .then(async ({ data }) => {
                    await Storage.setCookingInstructions(data);
                    return data;
                })
                .catch((err) => {
                    console.warn('[Sync] Failed to sync cooking instructions:', err?.message || err);
                    return null;
                });
        },
        syncData: async ({ dispatch, commit, getters }) => {
            // If device is offline, skip network sync silently
            try {
                const online = connectivityMonitor.getStatus();
                if (!online) {
                    // console.log('[Sync] Skipping syncData: device is offline');
                    return 'offline-skip';
                }
            } catch (e) { }

            const store = await Storage.getDefaultStore();
            await dispatch('getDeviceSettings')
            await dispatch("getCustomerDetails")
            let validate = await dispatch(`validatePCRegistration`);
            if (!validate.success) {
                await responses.deviceErr(validate.message, `Registration failed`)
                commit(`setDefaultStore`, null);
                return router.push({ name: `setup` })
            }
            if (store) {
                try {
                    let deviceName = '';
                    try {
                        if (Capacitor.isNativePlatform() || window?.androidMode) {
                            deviceName = await Device.getPCName(true);
                        } else if (window?.hardwareIPC || window?.electronBridge) {
                            const data = await hardwareBridge.getPcData();
                            deviceName = data?.hostname || '';
                        }
                    } catch (e) {
                        // ignore hardware bridge errors
                    }

                    const { data } = await axios.get(
                        serverApi + `invoice/${store.StoreDB}/offline/latest?deviceName=${encodeURIComponent(deviceName)}`
                    );

                    const latestSequence = Number(data?.latestSequence);
                    if (Number.isFinite(latestSequence) && latestSequence >= 0) {
                        // This store's sequence only (per-store counter key)
                        await resetCounter(latestSequence, store.StoreDB);
                        try {
                            localStorage.setItem('latestOfflineInvoiceSequence', String(latestSequence));
                        } catch (e) {
                            // ignore localStorage failures
                        }
                    }
                } catch (e) {
                    // Non-blocking: continue even if server call fails
                    console.warn('[Sync] Failed to fetch latest offline invoice sequence:', e);
                }
            }


            const isNativeMobile = (() => {
                try {
                    if (window?.androidMode) return true;
                    return !!(window?.Capacitor &&
                        typeof window.Capacitor.isNativePlatform === 'function' &&
                        window.Capacitor.isNativePlatform());
                } catch (_) {
                    return !!window?.androidMode;
                }
            })();
            const syncTasks = [
                dispatch("getExpiryDateByCustomerID"),
                dispatch("updateToLatestVersion"),
                dispatch('getAllStock'),
                dispatch('getAllUsers'),
                dispatch('getBaseCurrency'),
                dispatch('getAllCurrencies'),
                dispatch("getCategories"),
                dispatch("isKazangIntegrated"),
                dispatch("getAllPaymentMethods"),
                dispatch("getAllActiveSpecials"),
                dispatch("getAllTaxLabels"),
                dispatch("getAllStores"),
                dispatch("getAllFranchiseSettings"),
                dispatch("getAllSecurities"),
                dispatch("getSystemSettings"),
                dispatch("isLoyaltyActivated"),
                dispatch("isZedMobileActivated"),
            ];
            if (!isNativeMobile) {
                syncTasks.push(dispatch("syncCookingInstructions"));
            }

            return axios.all(syncTasks).then(() => {
                commit("setKazangSession");
                commit('setLastUpdated', CurrentTime())
            });

        },
        appLogin: async (context, payload) => {
            const loginPayload = { ...payload, deviceId: await Device.getDeviceId() }
            return axios.post(backOfficeTomcatApi + 'mobileUser/login', loginPayload)
                .then(async ({ data }) => {
                    // Franchise Welcome footprint — kept on store-only sign-out.
                    await Storage.setAdminUser(loginPayload)
                    // Persist picker list to SQLite so it survives restart without
                    // relying only on vuex-persist.
                    await Storage.setAllStores(Array.isArray(data) ? data : [])

                    // Extract mainCustomerID from the first store in the response
                    if (data && data.length > 0 && data[0].mainCustomerID) {
                        await context.commit('setMainCustomerID', data[0].mainCustomerID)
                    }
                }).catch((err) => errorHandler.tomcatError(err))
        },

        fetchStoresByCustomer: async (context) => {
            const mainCustomerID = context.getters.getMainCustomerID
            if (!mainCustomerID) {
                console.warn('No mainCustomerID found')
                return
            }

            return axios.get(subscriptionApi + `customer/getStoresByCustomer?mainCustomerID=${mainCustomerID}`)
                .then(async ({ data }) => {
                    await context.commit('setCustomerStores', data)
                    return data
                })
                .catch((err) => {
                    errorHandler.tomcatError(err)
                    throw err
                })
        },

        isZedMobileActivated: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(zedMobileApi + 'auth/lytsoft/checkZedMobileStatus', {
                headers: {
                    DB: window.btoa(store.StoreDB)
                }
            }).then(({ data }) => {
                context.commit(`setZedMobileStatus`, data)
                if (!!data) {
                    return axios.get(zedMobileApi + 'products/getCategories', {
                        headers: { "Api-key": data }
                    }).then(response => context.commit(`setZedMobileCategories`, response.data))
                }
            }).catch((err) => errorHandler.tomcatError(err))
        },

        getCustomerDetails: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `stores/getRegistrationDetails`, {
                params: { database: store.StoreDB }
            }).then(({ data }) => context.commit(`setRegistrationDetails`, data))
                .catch((err) => errorHandler.tomcatError(err))
        },

        confirmationText: (context, payload) => {
            return Swal.fire({
                title: 'Authorization',
                icon: 'info',
                html:
                    '<div style="font-family: Arial, sans-serif;' +
                    '        font-size: 16px;' +
                    '        line-height: 1.5;' +
                    '        color: #333;' +
                    '        background-color: #f7f7f7;' +
                    '        padding: 20px;' +
                    '        border: 1px solid #ccc;' +
                    '        border-radius: 5px;' +
                    '        margin: 20px 0;">' +
                    '<p><strong>Please note:</strong> By clicking on the "Confirm" button, you are authorizing the ' +
                    'automatic monthly deduction of K50.00 from your Kazang account as part of the subscription model ' +
                    'for this software.</p></div>',
                showCloseButton: false,
                showCancelButton: true,
                confirmButtonText: `Confirm`,
                focusConfirm: false,
            }).then((res) => {
                if (res.isConfirmed) {
                    return context.dispatch(`setupStore`, payload)
                }
            })
        },

        setupStore: async (context, payload) => {
            return Swal.fire({
                title: 'Please Enter your TPIN',
                icon: 'question',
                input: 'tel',
                showCancelButton: true,
                confirmButtonText: 'Confirm',
                showLoaderOnConfirm: true,
                allowOutsideClick: false,
                preConfirm: async (inputValue) => {
                    payload.DeviceID = await Device.getDeviceId();
                    payload.data = await Storage.getAdminUser();
                    payload.tpin = inputValue;
                    return axios.post(franchiseTomcatApi + `AndroidLogin/SecondeReg`, payload)
                        .then(({ data }) => context.dispatch(`loginToStore`, data))
                        .catch((err) => errorHandler.tomcatError(err))
                },
            });
        },

        loginToStore: async (context, payload) => {
            await context.commit(`setDefaultStore`, payload);
            await context.dispatch(`syncDataLogin`);
        },
        getDeviceSettings: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            let android = window.androidMode;

            return Device.getPCName(android).then(device => {
                return axios.get(serverApi + `settings/${store.StoreDB}/${device}`)
                    .then(({ data }) => context.commit('setDeviceSettings', data))
                    .catch((err) => errorHandler.tomcatError(err, false, 'Error on Settings'))
            }).catch(err => errorHandler.tomcatError(err, false, 'Error on Settings'))
        },

        getFloatUsers: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `cashup/${store.StoreDB}/noneFloat`)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        assignFloat: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            payload.device = settings.PC
            return axios.post(`${serverApi}float/assign/${store.StoreDB}`, payload)
                .then(({ data }) => {
                    if (!data.assigned) {
                        responses.throwErr(data.message)
                        return false;
                    }
                    context.commit('setUserFloat', {
                        user: payload.user,
                        device: payload.device,
                        supervisor: data.supervisor,
                        float: Number(payload.amount)
                    });
                    return true;
                }).catch((err) => errorHandler.tomcatError(err))
        },
        checkFloat: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(`${serverApi}float/check/${store.StoreDB}`, payload)
                .catch((err) => errorHandler.tomcatError(err))
        },
        getcashUpDiff: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(`${serverApi}cashup/${store.StoreDB}/status`, payload)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        checkUserCashedUpToday: async (context, username) => {
            let store = await Storage.getDefaultStore();
            const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
            const payload = {
                user: username,
                date: today
            };
            return axios.post(franchiseTomcatApi + `cashup/${store.StoreDB}/search`, payload)
                .then(({ data }) => {
                    // If data is an array and has cashups, user has already cashed up today
                    return data && Array.isArray(data) && data.length > 0;
                })
                .catch((err) => {
                    console.error('Error checking cashup status:', err);
                    return false; // On error, allow them to proceed
                })
        },
        saveSettings: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            const online = connectivityMonitor.getStatus();
            if (!online) {
                try {
                    // Save locally and queue for later sync
                    await Storage.setDeviceSettings(payload);
                    settingsOutbox.enqueueSettings(payload);
                    // Commit immediately so UI reflects changes
                    context.commit('setDeviceSettings', payload);
                    responses.showInfo('Settings saved locally. Will sync when back online.');
                    return true;
                } catch (e) {
                    return errorHandler.tomcatError(e, false, 'Failed to save settings offline');
                }
            }
            return axios.post(`${serverApi}settings/${store.StoreDB}`, payload).then(async ({ data }) => {
                context.commit('setDeviceSettings', data)
                // Clear any queued settings since server now has latest
                settingsOutbox.clearQueuedSettings();
                return context.dispatch(`syncData`);
            }).catch(async (err) => {
                // Network/server error while online; fallback to queue locally
                try {
                    await Storage.setDeviceSettings(payload);
                    settingsOutbox.enqueueSettings(payload);
                    context.commit('setDeviceSettings', payload);
                    responses.showWarning('Network issue. Settings saved locally and will sync later.');
                    return true;
                } catch (e) {
                    return errorHandler.tomcatError(err);
                }
            })
        },
        syncDeviceSettingsOutbox: async (context) => {
            try {
                const queued = settingsOutbox.getQueuedSettings();
                if (!queued) return false;
                let store = await Storage.getDefaultStore();
                const { data } = await axios.post(`${serverApi}settings/${store.StoreDB}`, queued);
                context.commit('setDeviceSettings', data);
                settingsOutbox.clearQueuedSettings();
                responses.showSuccess('Device settings synced successfully.');
                return true;
            } catch (err) {
                // Keep queued; will retry on next online event
                console.warn('[settingsOutbox] Sync failed; will retry later:', err?.message || err);
                return false;
            }
        },
        getAllPrinters: async () => {
            const electron = !!(window?.hardwareIPC || window?.electronBridge || window?.process?.versions?.electron);
            try {
                // Prefer Electron IPC when available
                if (electron) {
                    const list = await hardwareBridge.getPrinters();
                    // Ensure array of strings
                    return Array.isArray(list) ? list : [];
                }
            } catch (e) {
                console.warn('Electron printers fallback due to error:', e);
            }
            // Local Hardware Service removed; no web fallback
            return []
        },
        getStockLocations: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(`${serverApi}locations/${store.StoreDB}`)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        cashUpUser: async (context, payload) => {
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `CompleteCashup`);
            if (!isAllowed) throw new Error(`User not allowed to Cashup`);
            let store = await Storage.getDefaultStore()
            let device = await Storage.getDeviceSettings();
            payload.PC = device.PC

            let float = await Storage.getFloat()
            float = float ? float.filter(f => f.user !== payload.user) : [];
            $utils.showLoading();
            return axios.post(`${serverApi}cashup/${store.StoreDB}`, payload)
                .then(({ data }) => {
                    let cashupNumber = data.response.cashupNum;
                    context.commit('setUserFloat', float);
                    context.dispatch(`printCashup`, cashupNumber).catch(err => console.error('Print error:', err));
                    return responses.showInfo('Cash up completed successfully');
                }).catch((err) => errorHandler.tomcatError(err))
        },

        printCashup: async (context, cashupNumber) => {
            let isAllowed = await context.dispatch(`validateSecurityPermission`, `PrintCashUp`);
            if (!isAllowed) throw new Error(`User not allowed to print cashup`);
            let user = await Storage.getCurrentUser();
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            if (settings.CashupPrintStyle !== `SLIP` || settings.CashupPrintStyle == `0`) {
                const apiUrl = `https://chrilantech.com/LytSoftPosOnline/api/PDFReport/CashUp/${cashupNumber}/${store.StoreDB}`;
                return window.open(apiUrl, '_blank');

            }
            else {

                try {

                    let store = await Storage.getDefaultStore();
                    let settings = await Storage.getDeviceSettings();
                    let pcName = settings.PC;
                    const response = await axios.post(printVoid + `printing/${store.StoreDB}/printCashup/${cashupNumber}/${user.Username}?PC=${pcName}`, settings);

                    if (androidMode) {
                        return window.location.href = `rawbt:base64,${response.data}`
                    }

                    if (settings.Printer) {
                        // Prefer Electron IPC when available
                        if (window?.hardwareIPC || window?.electronBridge) {
                            const ok = await hardwareBridge.printBase64({
                                printer: settings.Printer,
                                base64: response.data
                            });
                            if (!ok) return responses.showWarning('Failed to send print to Electron printer');
                            return true;
                        }
                        // Fallback to Local Hardware Service
                        let payload = {
                            printer: settings.Printer,
                            base64: response.data
                        }
                        return axios.post(hardwareApi + 'printing/base64', payload)
                    } else {
                        return responses.showWarning('No printer selected')
                    }

                } catch (err) {
                    errorHandler.tomcatError(err, false, `Cashup Print Error`)
                }


            }
        },

        getBaseCurrency: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `currency/${store.StoreDB}/getBaseCurrency`)
                .then(({ data }) => context.commit('setBaseCurrency', data))
                .catch((err) => errorHandler.tomcatError(err))
        },
        upgradeKazangPOSCustomer: async (context, payload) => {
            let obj = queryStringify(payload);
            return axios.put(franchiseTomcatApi + `stores/upgradeKazangPOSCustomer?` + obj)
                .then(() => context.dispatch(`getCustomerDetails`))
                .catch((err) => errorHandler.tomcatError(err))
        },

        checkExpiry: async ({ getters }) => {
            // Prefer in-memory selected store to avoid stale validation right after switching stores
            let selectedStore = getters.getDefaultStore;
            let store = selectedStore || await Storage.getDefaultStore();
            if (!store) {
                return;
            }
            let storeDB = store.StoreDB;

            // Check if online
            const isOnline = connectivityMonitor.getStatus();

            if (!isOnline) {
                // Offline: Use cached expiry date
                //  console.log('[checkExpiry] Offline - using cached expiry date');
                const cachedExpiry = await Storage.getStoreExpiry();

                if (cachedExpiry) {
                    let expireDate = new Date(cachedExpiry);
                    let today = new Date();
                    const isExpired = expireDate < today;
                    //  console.log('[checkExpiry] Cached expiry:', cachedExpiry, 'Is expired:', isExpired);
                    return isExpired;
                } else {
                    console.warn('[checkExpiry] No cached expiry date available. Allowing login.');
                    // No cached expiry - allow login (benefit of the doubt when offline)
                    return false;
                }
            }

            // Online: Fetch from server and cache the result
            return axios.get(backOfficeApiGET, {
                params: {
                    route: `other/getExpiryDate?Database=` + storeDB,
                }
            }).then(async ({ data }) => {
                // Store expiry date for offline use
                await Storage.setStoreExpiry(data);
                //  console.log('[checkExpiry] Stored expiry date:', data);

                let expireDate = new Date(data);
                let today = new Date();
                return expireDate < today;
            }).catch(async (err) => {
                // Network error: Fallback to cached expiry
                console.warn('[checkExpiry] Failed to fetch expiry from server, using cache:', err.message);
                const cachedExpiry = await Storage.getStoreExpiry();

                if (cachedExpiry) {
                    let expireDate = new Date(cachedExpiry);
                    let today = new Date();
                    return expireDate < today;
                } else {
                    // No cache and network error - propagate error
                    throw err;
                }
            });
        },
        updateToLatestVersion: async () => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.put(franchiseTomcatApi + `version/${store.StoreDB}/updateToLatest`);
        },
        setDefaultStore: async (context, data) => {
            const nextDb = String(data?.StoreDB || '').trim();
            // Device stock is not keyed by StoreDB. Always wipe when binding
            // a store so a null previousDb (after sign-out / switch-store)
            // cannot skip clear and leave another store's catalogue.
            if (nextDb) {
                try {
                    await Storage.clearLocalStock();
                    context.commit('stockByPLU', { items: [], persist: false });
                    context.commit('setStockSetting', []);
                } catch (e) {
                    console.warn('[setDefaultStore] clearLocalStock failed:', e?.message || e);
                }
            }
            context.commit('setDefaultStore', data)
            try {
                await Storage.setDefaultStore(data);
            } catch (e) {
                console.warn('Failed to persist default store:', e);
            }
            try {
                if (window?.hardwareIPC || window?.electronBridge) {
                    await hardwareBridge.setStoreDB(data?.StoreDB || 'none');
                }
            } catch (e) {
                console.warn('Failed to persist StoreDB to Electron store:', e);
            }
            return context.dispatch('syncData')
        },

        validatePCRegistration: async (context) => {
            let deviceID = await Device.getDeviceId();
            let store = await Storage.getDefaultStore();
            const isAndroid = window.androidMode;

            let volumeId = null;
            try {
                if (window.hardwareIPC || window.electronBridge) {
                    const pcData = await hardwareBridge.getPcData();
                    if (pcData && pcData.VOLUMEID) {
                        volumeId = pcData.VOLUMEID;
                    }
                }
            } catch (e) {
                console.warn('Failed to retrieve Volume ID:', e);
            }

            if (!store) return { success: true, message: `Success` };
            let obj = {
                volumeId: volumeId,
                customerID: store.CustomerID,
                deviceID: deviceID,
                platform: isAndroid ? 'Lytning Android RetailPOS' : 'Lytning Retail POS'
            };

            return axios.get(subscriptionApi + `devices/validatePCRegistration`, { params: obj })
                .then(() => {
                    return { success: true, message: `Success` };
                }).catch(err => {
                    let errorMessage = errorHandler.franchiseLoginError(err);
                    return { success: false, message: errorMessage };
                });
        },
        getApikey: () => {
            // Example function to fetch the API key from an API endpoint
            fetch('https://example.com/api/getApiKey')
                .then(response => response.json())
                .then(data => {
                    // Assuming the API key is in data.apiKey
                    const apiKey = data.apiKey;
                    // Store the API key in local storage
                    localStorage.setItem('apiKey', apiKey);
                })
                .catch(error => {
                    console.error('Error fetching API key:', error);
                });
        },

        getSystemSettings: async (context) => {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `systemsetting/${store.StoreDB}`)
                .then(({ data }) => context.commit(`setSystemSettings`, data))
        },
        fetchResetCount: async (context) => {
            let storeDB = await Storage.getCurrentStoreDB();
            try {
                const response = await axios.get(`${subscriptionApi}devices/getResetStatusByStore?DB=${storeDB}`);
                context.commit('setResetCount', response.data);
                return response.data;
            } catch (err) {
                return errorHandler.tomcatError(err);
            }
        },
        ResetDevices: async (context, user) => {
            const store = await Storage.getDefaultStore();
            const payload = {
                user: user,
                DB: store.StoreDB,
            }
            try {
                const response = await axios.post(`${subscriptionApi}devices/resetDevices`, payload);
                return response.data;
            } catch (err) {
                // Check for specific error status codes
                if (err.response && (err.response.status === 400 || err.response.status === 401 || err.response.status === 404)) {
                    // Check if error message contains reset count information
                    const errorMessage = err.response.data?.message || err.message;
                    if (errorMessage.toLowerCase().includes('reset count')) {
                        return Swal.fire({
                            title: 'Reset Count Exceeded',
                            text: 'You are out of reset counts. Please purchase a license to continue.',
                            icon: 'warning',
                            showCancelButton: true,
                            showCloseButton: true,
                            confirmButtonText: 'Continue',
                            cancelButtonText: 'Logout',
                            allowOutsideClick: false
                        }).then(async (result) => {
                            if (result.isConfirmed) {
                                try {
                                    router.push({ name: `system-expired` })
                                } catch (e) {
                                }
                            } else {
                                await store.dispatch('signOutOfFranchise');
                            }
                        });
                    }
                }
                return errorHandler.tomcatError(err);
            }
        },

    },
    modules: {
        salesModule, stockModule, stockCategories, userModule, floatModule, tableModule, emailModule, kazangPayment,
        cookingInstruction, stockTake, paymentMethods, accounts, kazangModule, supplierModule, stockReceivedModule,
        stockTransfer: stockTransferModule, kazangSignUp, ticketModule, customInstall, billingModule, backOfficeModule, timeModule, taxModule,
        user_security_module, loyaltyModule, layByModule, vatCalculatorModule, ticket_notes_module, theme, windowModule,
        orders,
        updates: updatesModule
    },
    plugins: [
        vuexLocal.plugin,
        // Plugin to restore stockCache as Map after rehydration
        (store) => {
            store.subscribe((mutation, state) => {
                // Ensure stockCache is always a Map
                if (state.stockModule && state.stockModule.stockCache) {
                    if (!(state.stockModule.stockCache instanceof Map)) {
                        // Convert plain object back to Map
                        const cacheEntries = Object.entries(state.stockModule.stockCache);
                        store.state.stockModule.stockCache = new Map(cacheEntries);
                    }
                }
            });

            // Also fix on initial load
            if (store.state.stockModule && store.state.stockModule.stockCache) {
                if (!(store.state.stockModule.stockCache instanceof Map)) {
                    const cacheEntries = Object.entries(store.state.stockModule.stockCache);
                    store.state.stockModule.stockCache = new Map(cacheEntries);
                }
            }
        }
    ],
})

// Auto-sync device settings outbox on startup and when coming back online
try {
    if (connectivityMonitor.getStatus()) {
        store.dispatch('syncDeviceSettingsOutbox');
    }
    connectivityMonitor.subscribe('online', () => {
        store.dispatch('syncDeviceSettingsOutbox');
    });
} catch (e) {
    console.warn('[store] Failed to initialize settings outbox sync subscription:', e);
}

export default store;
